// AURAX_REDESIGN_2710 (GROK2): clone de DashboardXuiQuickTest — grade de caixinhas, 1 clique = teste.
// O cartão antigo continua no código (DashboardLegacy / TesteRapidoModal). Switch: private/TESTE_1CLIQUE_OFF.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Zap } from 'lucide-react';
import { API_BASE_URL } from '../apiBase';
import { formatApiError } from '../context/AuthContext';
import { cn } from '../lib/utils';
import NovoTesteCompacto from './NovoTesteCompacto'; // CRIAR_RAPIDO_2810
import { fetchAuraxFlags as fetchFlags2810 } from '../lib/auraxFlags';
import TesteRapidoUmClique from './TesteRapidoUmClique';
import { fetchDashStats } from '../lib/dashStats2810'; // DASH_STATS_2810
import './DashStats2810.css';
import { PainelTesteCards0311, painelTesteBody0311 } from './PainelFiltro0311'; // EQUIPE_V2_0311

function newRequestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function AppTile({ app, busy, disabled, onClick, big }) {
  const [logoOk, setLogoOk] = useState(true);
  const logo = app.logo_url && logoOk
    ? (app.logo_url.startsWith('http') ? app.logo_url : `${API_BASE_URL}${app.logo_url}`)
    : null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={app.ready ? `Gerar teste ${app.name}` : 'Sem plano liberado para teste rápido'}
      className={cn(
        'group relative flex flex-col items-center justify-center gap-2 rounded-2xl border bg-card p-4 text-center shadow-sm transition',
        big && 'qt2810-tile',
        'hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        disabled && 'cursor-not-allowed opacity-50 hover:translate-y-0 hover:shadow-sm',
      )}
    >
      <div className={cn('flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl', big && 'qt2810-logo')} style={logo ? undefined : { background: app.color }}>
        {logo ? (
          <img src={logo} alt="" className="h-full w-full object-contain" loading="lazy" decoding="async" onError={() => setLogoOk(false)} />
        ) : (
          <span className="text-lg font-bold tracking-wide text-white">{app.initials}</span>
        )}
      </div>
      <span className="line-clamp-2 text-sm font-semibold leading-tight" style={{ fontFamily: "'Poppins','Inter',system-ui,sans-serif" }}>
        {app.name}
      </span>
      {busy ? (
        <span className="absolute inset-0 flex items-center justify-center rounded-2xl bg-background/70">
          <Loader2 className="h-6 w-6 animate-spin" />
        </span>
      ) : null}
    </button>
  );
}

// QT_GRID_CACHE_2810 (GROK2): busca começa assim que o módulo carrega e a grade aparece
// na hora com o último resultado salvo no navegador. Switch: private/QT_GRID_CACHE_OFF.
const QT_LS_KEY = 'aurax_qt_apps_v1';
function readQtCache() {
  try { const v = JSON.parse(window.localStorage.getItem(QT_LS_KEY) || 'null'); return v && Array.isArray(v.apps) ? v : null; } catch (_) { return null; }
}
let qtPrefetch = null;
function fetchQtApps() {
  if (!qtPrefetch) {
    qtPrefetch = axios.get(`${API_BASE_URL}/api/xui/quick-test/apps`, { withCredentials: true, timeout: 30000 })
      .finally(() => { setTimeout(() => { qtPrefetch = null; }, 0); });
  }
  return qtPrefetch;
}
try { if (typeof window !== 'undefined' && window.location.pathname.startsWith('/dashboard')) fetchQtApps().catch(() => {}); } catch (_) { /* noop */ }

export default function DashboardQuickTestGrid({ className }) {
  const [state, setState] = useState(() => {
    const c = readQtCache();
    return c ? { loading: false, apps: c.apps, missing: c.missing || [], limit: c.limit_per_hour, used: c.used_last_hour || 0 }
      : { loading: true, apps: [], missing: [], limit: 20, used: 0 };
  });
  const [busyId, setBusyId] = useState('');
  const [cr2810, setCr2810] = useState(false);
  const [crOpen2810, setCrOpen2810] = useState(false);
  useEffect(() => { fetchFlags2810().then((f) => setCr2810(!!f?.criar_rapido_2810)).catch(() => {}); }, []);
  const [result, setResult] = useState(null);
  // DASH_STATS_2810: logos maiores + título curto (switch private/DASH_STATS_2810_OFF)
  const [big, setBig] = useState(true);
  useEffect(() => { fetchDashStats().then((r) => setBig(!!r.on)); }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await fetchQtApps();
      try {
        if (data.client_cache === false) window.localStorage.removeItem(QT_LS_KEY);
        else window.localStorage.setItem(QT_LS_KEY, JSON.stringify({ apps: data.apps || [], missing: data.missing || [], limit_per_hour: data.limit_per_hour, used_last_hour: data.used_last_hour }));
      } catch (_) { /* noop */ }
      setState({ loading: false, apps: data.apps || [], missing: data.missing || [], limit: data.limit_per_hour, used: data.used_last_hour });
    } catch (e) {
      setState((s) => (s.apps && s.apps.length ? { ...s, loading: false } : { ...s, loading: false, error: formatApiError(e?.response?.data?.detail) || 'Não foi possível carregar os aplicativos.' }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async (app) => {
    if (busyId || !app.ready) return; // proteção contra clique duplo (UI); o backend também trava por request_id
    setBusyId(app.id);
    const request_id = newRequestId();
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/xui/quick-test/one-click`,
        { partner_app_id: app.id, plan_id: app.plan?.id || '', app_name: app.name, request_id, ...painelTesteBody0311() },
        { withCredentials: true, timeout: 120000 },
      );
      setResult({ ...data, app });
      setState((s) => ({ ...s, used: (s.used || 0) + 1 }));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || 'Não foi possível gerar o teste.');
    } finally {
      setBusyId('');
    }
  };

  return (
    <>
    {cr2810 ? <NovoTesteCompacto open={crOpen2810} onOpenChange={setCrOpen2810} initialMode="cliente" /> : null}
    <PainelTesteCards0311 />
    <section className={cn('mb-4 rounded-2xl border bg-card/60 p-4', className)} aria-label="Teste rápido em 1 clique">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {big ? (<>
        <h2 className="flex items-center gap-2 text-base font-semibold"><Zap className="h-4 w-4 text-amber-500" />Clique no app que o teste sai na hora</h2>
        {state.limit ? <span className="text-xs text-muted-foreground">{`${state.used}/${state.limit} na última hora`}</span> : null}
        {cr2810 ? <button type="button" data-criar-cliente-2810 onClick={() => setCrOpen2810(true)} className="rounded-lg border border-primary px-3 py-1.5 text-sm font-semibold text-primary hover:bg-primary/10">+ Cliente</button> : null}
        </>) : (<>
        <h2 className="flex items-center gap-2 text-base font-semibold"><Zap className="h-4 w-4 text-amber-500" />Teste rápido</h2>
        <span className="text-xs text-muted-foreground">{state.limit ? `${state.used}/${state.limit} na última hora` : 'sem limite de testes'} · clique no app e o teste sai na hora</span>
        </>)}
      </div>
      {state.loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carregando aplicativos…</div>
      ) : state.error ? (
        <p className="text-sm text-destructive">{state.error}</p>
      ) : (
        <>
          <div className={big ? 'qt2810-grid' : 'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'} style={big ? { '--qt-cols': Math.min(6, Math.max(2, state.apps.length)) } : undefined}>
            {state.apps.map((app) => (
              <AppTile key={app.id} big={big} app={app} busy={busyId === app.id} disabled={!!busyId || !app.ready} onClick={() => generate(app)} />
            ))}
          </div>
          {state.missing.length ? (
            <ul className="qt-missing-hint mt-3 space-y-1 text-xs text-muted-foreground">
              {state.missing.map((m) => <li key={m}>• {m}</li>)}
              <li>
                Ajuste em <Link className="underline" to="/gestor-xui/partner-apps">Catálogo de aplicativos</Link> e{' '}
                <Link className="underline" to="/gestor-xui/categories">Planos</Link>.
              </li>
            </ul>
          ) : null}
        </>
      )}
      <TesteRapidoUmClique result={result} onClose={() => setResult(null)} />
    </section>
    </>
  );
}
